import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AppDownloadBanner from './components/AppDownloadBanner';
import Login from './pages/Login';
import Register from './pages/Register';
import EmailVerification from './pages/EmailVerification';
import ResendVerification from './pages/ResendVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import OAuthCallback from './pages/OAuthCallback';
import Home from './pages/Home';
import Decks from './pages/Decks';
import DeckDetail from './pages/DeckDetail';
import Quizzes from './pages/Quizzes';
import QuizDetail from './pages/QuizDetail';
import QuizPlay from './pages/QuizPlay';
import QuizResults from './pages/QuizResults';
import Study from './pages/Study';
import Match from './pages/Match';
import MatchPlay from './pages/MatchPlay';
import MatchDetail from './pages/MatchDetail';
import Studies from './pages/Studies';
import StudyModules from './pages/StudyModules';
import StudyModuleDetail from './pages/StudyModuleDetail';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import AdminUserEdit from './pages/AdminUserEdit';
import AdminContentManagement from './pages/AdminContentManagement';
import Pricing from './pages/Pricing';
import Docs from './pages/Docs';
import DocsIndex from './pages/docs/Index';
import Flashcards from './pages/docs/Flashcards';
import DocsQuizzes from './pages/docs/Quizzes';
import MatchGameFeature from './pages/MatchGameFeature';
import StudiesControl from './pages/docs/StudiesControl';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import MainLanding from './pages/MainLanding';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';
import ContentSearcher from './pages/ContentSearcher';
import PublicDeckDetail from './pages/PublicDeckDetail';
import PublicQuizDetail from './pages/PublicQuizDetail';
import PublicMatchDetail from './pages/PublicMatchDetail';
import Unsubscribe from './pages/Unsubscribe';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';

// Public Route component (redirects to decks if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/decks" replace />;
  }

  return <>{children}</>;
};

// Root Route component (shows main landing for both authenticated and non-authenticated users)
const RootRoute: React.FC = () => {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return <MainLanding />;
};

function AppRoutes() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Root route - shows main landing or redirects to decks */}
        <Route path="/" element={<RootRoute />} />
        
        {/* Public routes */}
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } />
        
        {/* Authentication routes - can be accessed when logged in or out */}
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/resend-verification" element={<ResendVerification />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />

        {/* Legal pages - accessible without authentication */}
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        
        {/* Landing pages - accessible without authentication */}
        <Route path="/main" element={<MainLanding />} />

        {/* Blog routes - accessible without authentication */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />

        {/* Public deck routes - accessible without authentication */}
        <Route path="/search" element={<ContentSearcher />} />
        <Route path="/flashcards/:slug" element={<PublicDeckDetail />} />
        <Route path="/quizzes/:slug" element={<PublicQuizDetail />} />
        <Route path="/matches/:slug" element={<PublicMatchDetail />} />

        {/* Protected routes */}
        <Route path="/home" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Home />} />
        </Route>
        <Route path="/decks" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Decks />} />
          <Route path="management/:id" element={<DeckDetail />} />
        </Route>
        <Route path="/quizzes" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Quizzes />} />
          <Route path="management/:id" element={<QuizDetail />} />
        </Route>
        <Route path="/quiz/:id" element={
          <ProtectedRoute>
            <QuizPlay />
          </ProtectedRoute>
        } />
        <Route path="/quiz/:id/results" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<QuizResults />} />
        </Route>
        <Route path="/study" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Study />} />
          <Route path=":deckId" element={<Study />} />
        </Route>
        <Route path="/matches" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Match />} />
          <Route path="management/:id" element={<MatchDetail />} />
        </Route>
        <Route path="/matches/:id/play" element={
          <ProtectedRoute>
            <MatchPlay />
          </ProtectedRoute>
        } />
        <Route path="/studies" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Studies />} />
          <Route path=":id" element={<StudyModules />} />
          <Route path=":id/module/:moduleId" element={<StudyModuleDetail />} />
        </Route>
        <Route path="/profile" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Profile />} />
        </Route>

        <Route path="/pricing" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Pricing />} />
        </Route>
        <Route path="/admin" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Admin />} />
          <Route path="users/:userId/edit" element={<AdminUserEdit />} />
          <Route path="content-management" element={<AdminContentManagement />} />
        </Route>

        <Route path="/feature/match-game" element={<MatchGameFeature />} />

        {/* Documentation routes - accessible without authentication */}
        <Route path="/docs" element={<Docs />}>
          <Route index element={<DocsIndex />} />
          <Route path="flashcards" element={<Flashcards />} />
          <Route path="quizzes" element={<DocsQuizzes />} />
          <Route path="match-game" element={<Navigate to="/feature/match-game" replace />} />
          <Route path="studies" element={<StudiesControl />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

// Theme-aware Toaster component
const ThemedToaster: React.FC = () => {
  const { isDark } = useTheme();
  
  return (
    <Toaster
      position="top-right"
      containerStyle={{
        top: '20px',
        zIndex: '99999',
        position: 'fixed',
      }}
      gutter={8}
      containerClassName="toast-container"
      toastOptions={{
        duration: 4000,
        style: {
          background: isDark ? '#374151' : '#fff',
          color: isDark ? '#f9fafb' : '#333',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          padding: '16px',
          paddingRight: '40px',
        },
        // Add custom close button for all toasts
        success: {
          iconTheme: {
            primary: isDark ? '#10b981' : '#059669',
            secondary: isDark ? '#374151' : '#fff',
          },
        },
        error: {
          iconTheme: {
            primary: isDark ? '#ef4444' : '#dc2626',
            secondary: isDark ? '#374151' : '#fff',
          },
        },
      }}
    >
      {(t) => {
        const handleClose = () => {
          toast.remove(t.id);
        };
        
        return (
          <div 
            style={{ 
              position: 'relative', 
              display: 'flex', 
              alignItems: 'center',
              background: isDark ? '#374151' : '#fff',
              color: isDark ? '#f9fafb' : '#333',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              padding: '16px',
              paddingRight: '40px',
              minWidth: '200px',
            }}
          >
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {t.icon}
              <div>{t.message as any}</div>
            </div>
            <button
              onClick={handleClose}
              onMouseDown={handleClose}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isDark ? '#9ca3af' : '#6b7280',
                transition: 'color 0.2s',
                zIndex: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = isDark ? '#f9fafb' : '#111827';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = isDark ? '#9ca3af' : '#6b7280';
              }}
              aria-label="Close notification"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                style={{ width: '16px', height: '16px' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        );
      }}
    </Toaster>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <div className="App">
              <AppDownloadBanner />
              <AppRoutes />
              <ThemedToaster />
            </div>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
